import { useEffect, useRef, useState, useCallback } from 'react'
import { useUpdate } from './useUpdate.js'
import { useClient } from '@based/sdk/react'
import { Filter, Sort, useBasedSchema, useDependency } from '../index.js'

export type PageState = {
  pageIndex: number
  pageSize: number
  sort: Sort
  filter: Filter
  search: string
}

export const usePagedSubscription = ({
  endpoint,
  lang = 'en',
  schemaType = '',
  bufferPageSize = 3,
  include = undefined,
}: {
  endpoint: string
  lang?: string
  schemaType: string
  bufferPageSize?: number
  include?: string[]
}) => {
  const client = useClient()
  const { data: schema } = useBasedSchema()

  const subsRef = useRef<null | (() => void)>(null)
  const dataRef = useRef<any[]>([])
  const activeSigRef = useRef<string>('')
  const lastOffsetLenSigRef = useRef<string>('')

  const [total, setTotal] = useState(0)
  const [checksum, setChecksum] = useState(0)
  const [pageIndex, setPageIndex] = useState(0)

  const update = useUpdate()

  const resetData = useCallback(
    ({ pageSize, sort, filter, search }: Partial<PageState>) => {
      subsRef.current?.()
      subsRef.current = null

      activeSigRef.current = ''
      lastOffsetLenSigRef.current = ''

      dataRef.current = []
      setTotal(0)
      setPageIndex(0)

      getPage({
        pageIndex: 0,
        pageSize,
        sort,
        filter,
        search,
      } as PageState)
    },
    [],
  )

  const mergePage = useCallback(
    (offset: number, pageData: any[]) => {
      const prev = dataRef.current
      let same = true

      if (offset + pageData.length > prev.length) {
        same = false
      } else {
        for (let i = 0; i < pageData.length; i++) {
          if (prev[offset + i] !== pageData[i]) {
            same = false
            break
          }
        }
      }

      const offsetLenSig = `${offset}|${pageData.length}|${same ? 1 : 0}`
      if (same && lastOffsetLenSigRef.current === offsetLenSig) {
        return
      }
      lastOffsetLenSigRef.current = offsetLenSig

      const next = prev.slice()

      if (offset === 0) {
        next.length = 0
        next.push(...pageData)
      } else {
        next.splice(offset, pageData.length, ...pageData)
      }

      const filtered = next.filter((x) => x != null)
      dataRef.current = filtered

      const nextTotal = Math.max(total, offset + pageData.length)
      if (nextTotal !== total) setTotal(nextTotal)

      update()
    },
    [update, total],
  )

  const getPage = useCallback(
    ({ pageIndex, pageSize, sort, filter, search }: PageState) => {
      if (pageSize == null) return

      const resolvedInclude =
        include && include.length
          ? include
          : Object.keys(schema?.types?.[schemaType]?.props || {})

      if (!resolvedInclude || resolvedInclude.length === 0) {
        return
      }

      const offset = pageIndex * pageSize
      const limit = pageSize * bufferPageSize

      const sig = JSON.stringify({
        endpoint,
        offset,
        limit,
        type: schemaType,
        sort,
        filter,
        $$lang: lang,
        include: resolvedInclude,
        search,
      })

      if (activeSigRef.current === sig) {
        return
      }
      activeSigRef.current = sig

      if (subsRef.current) {
        subsRef.current()
        subsRef.current = null
      }

      setPageIndex((prev) => (prev === pageIndex ? prev : pageIndex))

      console.log('[BasedFinder] Query: ', {
        endpoint,
        offset,
        limit,
        type: schemaType,
        sort,
        filter,
        $$lang: lang,
        include: resolvedInclude,
        search,
      })

      const close = client
        .query(endpoint, {
          offset,
          limit,
          type: schemaType,
          sort,
          filter,
          $$lang: lang,
          include: resolvedInclude,
          search,
        })
        .subscribe(
          (data: any[], nextChecksum: number) => {
            console.log('[BasedFinder] Data chunk: ', data)

            mergePage(offset, data)

            setChecksum((prev) => (prev === nextChecksum ? prev : nextChecksum))
          },
          (err) => {
            console.error('[BasedFinder] Data error: ', err)
          },
        )

      subsRef.current = close
    },
    [
      client,
      endpoint,
      schemaType,
      lang,
      bufferPageSize,
      mergePage,
      useDependency(include),
      useDependency(schema),
    ],
  )

  useEffect(() => {
    return () => {
      subsRef.current?.()
      subsRef.current = null
    }
  }, [])

  return {
    data: dataRef.current,
    checksum,
    total,
    pageIndex,
    getPage,
    resetData,
  }
}
